import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { logger } from "@/lib/logger";

const DISMISS_KEY = "fodindin:pwa-install-dismissed";
const DISMISS_TTL_DAYS = 7;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function wasRecentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return days < DISMISS_TTL_DAYS;
  } catch {
    return false;
  }
}

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installing, setInstalling] = useState(false);
  const [hidden, setHidden] = useState(true);
  // iOS users don't get beforeinstallprompt; show a guide instead.
  const [iosGuide, setIosGuide] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    const ua = (typeof window !== "undefined" && window.navigator?.userAgent) || "";
    const isIOS = /iphone|ipad|ipod/i.test(ua) && !/crios|fxios/i.test(ua);
    if (isIOS) {
      // Delay before showing to avoid immediate intrusion
      const t = setTimeout(() => {
        setIosGuide(true);
        setHidden(false);
      }, 12000);
      return () => clearTimeout(t);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setHidden(false);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const onInstalled = () => {
      setHidden(true);
      setDeferred(null);
      try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (hidden) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setHidden(true);
  };

  const install = async () => {
    if (!deferred) return;
    setInstalling(true);
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") {
        setHidden(true);
      } else {
        dismiss();
      }
    } catch (e) {
      logger.warn("PWA install failed:", e?.message);
    } finally {
      setDeferred(null);
      setInstalling(false);
    }
  };

  return (
    <div
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-40 bg-white rounded-2xl shadow-2xl border border-orange-200 p-4 flex items-start gap-3 animate-slide-in-up"
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-label="Installer l'application Fodindin"
    >
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-orange-400 text-white grid place-items-center shrink-0">
        <Smartphone size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm">Installer Fodindin sur votre telephone</div>
        {iosGuide ? (
          <p className="text-xs text-[var(--ink-soft)] mt-1">
            Touchez l&apos;icone <b>Partager</b> en bas de Safari, puis <b>&laquo; Sur l&apos;ecran d&apos;accueil &raquo;</b>.
          </p>
        ) : (
          <p className="text-xs text-[var(--ink-soft)] mt-1">
            Acces rapide, notifications, et ouverture plein ecran. Gratuit et instantane.
          </p>
        )}
        <div className="flex gap-2 mt-3">
          {!iosGuide && (
            <button
              type="button"
              onClick={install}
              disabled={installing || !deferred}
              className="btn-primary !text-xs inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="pwa-install-btn"
            >
              <Download size={12} /> {installing ? "Installation..." : "Installer"}
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            className="text-xs font-semibold text-[var(--ink-soft)] hover:bg-orange-50 px-3 py-1.5 rounded-full"
            data-testid="pwa-dismiss-btn"
          >
            Plus tard
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="p-1 rounded-full hover:bg-orange-50 shrink-0"
        aria-label="Fermer"
        data-testid="pwa-close-btn"
      >
        <X size={14} />
      </button>
    </div>
  );
}
